import { Filter, Query, TimeDimension } from '@cubejs-client/core';
import {
  AIClass,
  AxesLabelsAndDropdownOptions,
  BlueberryFeatures,
  BlueberryFlowerStageFeatures,
  ChartDropdownDimensionEnum,
  CubeDimensionEnum,
  FeatureEnum,
  FeatureInfo,
  ModelNamesBesidesFeatureEnums,
} from './cubeSlice';

// Service priority order, the Services will be prioritized over the version and the Services will be prioritize in the order of the array with the first element being the highest priority
export const priorityServicesOverVersion: Omit<
  (FeatureEnum | ModelNamesBesidesFeatureEnums)[],
  FeatureEnum.none
> = [
  ModelNamesBesidesFeatureEnums.grape_late_season,
  ModelNamesBesidesFeatureEnums.grape_mid_season,
  ModelNamesBesidesFeatureEnums.grape_early_mid_season,
];

/**
 * Find the highest model version from a list of AI classes that matches a specific class name
 *
 * @param aiClasses - Array of AIClass objects to search
 * @param feature - The class name to search for
 *
 * @returns Highest version number as a string if any AI class object matches the class name, otherwise null
 *
 * The function accounts for two possible formats of model versions:
 * 1. Numeric versions (e.g. '1', '2', '3')
 * 2. Word-number versions (e.g. 'version1', 'model2')
 * If any model versions are in the word-number format, it will prioritize numeric version first.
 * If all model versions are numeric, it will return the highest number.
 */
export function findHighestModelVersion(
  aiClasses: AIClass[],
  features: FeatureEnum[]
): AIClass | null {
  // Filter for AI classes that match the specified class name
  const filteredClasses = aiClasses.filter((aiClass) =>
    features.find((el) => el === aiClass[CubeDimensionEnum.className])
  );

  if (filteredClasses.length === 0) {
    return null;
  }

  // Filter for numeric versions and word-number versions
  const numericClasses = filteredClasses.filter((aiClass) =>
    /^[0-9.]+$/.test(aiClass[CubeDimensionEnum.modelVersion])
  );
  const wordNumClasses = filteredClasses.filter(
    (aiClass) =>
      /[a-zA-Z]+\d+/.test(aiClass[CubeDimensionEnum.modelVersion]) ||
      /\d+[a-zA-Z]/.test(aiClass[CubeDimensionEnum.modelVersion])
  );

  // Sort both arrays in descending order
  // If model versions different - compare by model version
  // If model version are same - order by model name
  // it's mostly inflorescence_juice change to prioritize
  // specific case models when it have collision with standard
  // model name with same model version
  numericClasses.sort((a, b) => {
    // FIXME: special case for grape_early_mid_season
    // if models are different and one of them is not prioritized - compare as usual
    if (a[CubeDimensionEnum.modelName] !== b[CubeDimensionEnum.modelName]) {
      if (
        priorityServicesOverVersion.includes(a[CubeDimensionEnum.modelName])
      ) {
        return -1;
      }
      if (
        priorityServicesOverVersion.includes(b[CubeDimensionEnum.modelName])
      ) {
        return 1;
      }
    }
    if (
      b[CubeDimensionEnum.modelVersion] !== a[CubeDimensionEnum.modelVersion]
    ) {
      return (
        parseFloat(b[CubeDimensionEnum.modelVersion]) -
        parseFloat(a[CubeDimensionEnum.modelVersion])
      );
    } else {
      // If modelVersion is the same, compare by modelName in descending order
      return b[CubeDimensionEnum.modelName].localeCompare(
        a[CubeDimensionEnum.modelName]
      );
    }
  });
  wordNumClasses.sort((a, b) => {
    // FIXME: special case for grape_early_mid_season
    // if models are different and one of them is not prioritized - compare as usual
    if (a[CubeDimensionEnum.modelName] !== b[CubeDimensionEnum.modelName]) {
      if (
        priorityServicesOverVersion.includes(a[CubeDimensionEnum.modelName])
      ) {
        return -1;
      }
      if (
        priorityServicesOverVersion.includes(b[CubeDimensionEnum.modelName])
      ) {
        return 1;
      }
    }
    const aMatch = a[CubeDimensionEnum.modelVersion].match(/\d+/);
    const bMatch = b[CubeDimensionEnum.modelVersion].match(/\d+/);
    const aNumber = aMatch ? parseFloat(aMatch[0]) : 0;
    const bNumber = bMatch ? parseFloat(bMatch[0]) : 0;
    return bNumber - aNumber;
  });

  // Return the highest version. Numeric versions are prioritized.
  // If neither type is found, returns null.
  if (numericClasses.length > 0) {
    return numericClasses[0];
  } else if (wordNumClasses.length > 0) {
    return wordNumClasses[0];
  } else {
    return null;
  }
}

// Clusters and Inflorescence were combined
export const FeatureAliases: { [key: string]: FeatureEnum[] } = {
  [FeatureEnum.clusters]: [FeatureEnum.clusters, FeatureEnum.inflorescence],
  [FeatureEnum.blueberry]: [
    FeatureEnum.blueberry,
    ...BlueberryFeatures,
    FeatureEnum.flower,
    ...BlueberryFlowerStageFeatures,
  ],
};

/**
 * Maps the input FeatureEnum to its corresponding alias in FeatureAliases, if exists.
 *
 * @param feature The input FeatureEnum to be mapped.
 * @returns The alias key corresponding to the input FeatureEnum, or the input itself if not found.
 */
export function mapToAlias(feature: FeatureEnum): FeatureEnum {
  // Iterate through the keys and values in FeatureAliases
  for (const [key, aliases] of Object.entries(FeatureAliases)) {
    // If the array of aliases includes the input feature, return the key as the alias
    if (aliases.includes(feature)) {
      return key as FeatureEnum;
    }
  }

  // If no alias found, return the input feature itself
  return feature;
}

/**
 * Returns a new query with the updated parameters
 * @param query Query to update
 * @param scanId New scanID to use
 * @param pointId New pointId to use
 * @param customerId New customerId to use
 * @param {number} [daysSinceNowServerSideScanDateFilter] - Optional number of days since the current date to filter the scans by server side.
 * @returns Updated query
 */
export const updateQueryParams = (
  query: Query,
  scanId?: string,
  pointId?: string,
  customerId?: string,
  daysSinceNowServerSideScanDateFilter?: number | null
): Query => {
  const newQuery = { ...query };
  // Initialize filters if they don't exist

  const filters: Filter[] = newQuery.filters ? [...newQuery.filters] : [];
  const timeDimensions: TimeDimension[] = newQuery.timeDimensions
    ? [...newQuery.timeDimensions]
    : [];

  if (scanId) {
    const scanIdFilter: Filter = {
      member: CubeDimensionEnum.detectionFactScanId,
      operator: 'equals',
      values: [scanId],
    };
    filters.push(scanIdFilter);
  }

  if (pointId) {
    const pointIdFilter: Filter = {
      member: CubeDimensionEnum.detectionFactPointId,
      operator: 'equals',
      values: [pointId],
    };
    filters.push(pointIdFilter);
  }

  if (customerId) {
    const customerIdFilter: Filter = {
      member: CubeDimensionEnum.detectionFactCustomerId,
      operator: 'equals',
      values: [customerId],
    };
    filters.push(customerIdFilter);
  }

  if (daysSinceNowServerSideScanDateFilter) {
    const scanDateFilter: TimeDimension = {
      dimension: CubeDimensionEnum.scanDate,
      dateRange: `last ${daysSinceNowServerSideScanDateFilter} days`,
    };
    timeDimensions.push(scanDateFilter);
  }

  return {
    ...newQuery,
    filters,
    ...(timeDimensions.length > 0 ? { timeDimensions } : {}),
  };
};

export const rowsideFilter = (
  currRowNum: string,
  currViewDirection: string
): Filter[] => [
  {
    member: CubeDimensionEnum.rowRowNum,
    operator: 'equals',
    values: [`${currRowNum || ''}`],
  },
  {
    member: CubeDimensionEnum.rowsideViewDirection,
    operator: 'equals',
    values: [`${currViewDirection || ''}`],
  },
];

/**
 * Finds the AxesLabelsAndDropdownOptions object in the FeatureInfo that matches the given ChartDropdownDimensionEnum and FeatureEnum.
 * @param dimension The ChartDropdownDimensionEnum to match.
 * @param feature The FeatureEnum to match.
 * @param FeatureInfo The FeatureInfo to search through.
 * @returns The AxesLabelsAndDropdownOptions object that matches the given ChartDropdownDimensionEnum and FeatureEnum, or null if no match is found.
 */
export function findAxesLabelsAndDropdownOptions(
  dimension: ChartDropdownDimensionEnum,
  feature: FeatureEnum,
  FeatureInfo: FeatureInfo
): AxesLabelsAndDropdownOptions | null {
  const optionsArray = FeatureInfo[feature];
  if (optionsArray) {
    for (const option of optionsArray) {
      if (option.dimension === dimension) {
        return option;
      }
    }
  }
  return null;
}

/**
 * Parses JSON strings in the first property value of the input object,
 * which is expected to be a JSON string containing an object with unknown property names.
 *
 * @param dataItem - The input object with key-value pairs, where the first value is a JSON string.
 * @returns An object containing the parsed values for the properties of the first value in the input object.
 */
export function parseNestedJSONInDataItem(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dataItem: { [key: string]: any }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): { [key: string]: any } {
  if (!dataItem) {
    console.warn('The dataItem is empty or invalid.');
    return {};
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const parsedDataItem: { [key: string]: any } = {};

  try {
    // eslint-disable-next-line
    const [firstKey, firstValue] = Object.entries(dataItem)[0];
    if (typeof firstValue === 'string') {
      const nestedObject = JSON.parse(firstValue);

      Object.entries(nestedObject).forEach(([key, value]) => {
        if (typeof value === 'string') {
          try {
            const parsedValue = JSON.parse(value);
            parsedDataItem[key] = parsedValue;
          } catch (error) {
            console.warn(
              `Error parsing JSON string for key ${key} in nested object`,
              error
            );
            parsedDataItem[key] = value;
          }
        } else {
          parsedDataItem[key] = value;
        }
      });
    } else {
      console.warn('The first value in dataItem is not a JSON string.');
    }
  } catch (error) {
    console.error('Error parsing JSON string in cube validation', error);
  }

  return parsedDataItem;
}

// Helper functions ----------------------------------------------------------------------------------------------------

/**
 * This function takes in a FeatureEnum value and checks if it is an alias for another FeatureEnum
 * value in the FeatureAliases object. If it is, the function returns the FeatureEnum that it is
 * an alias for. If it's not an alias, it just returns the original FeatureEnum value.
 *
 * @param {FeatureEnum} featureEnum - The input FeatureEnum value to check and possibly map.
 * @returns {FeatureEnum} - The mapped FeatureEnum value if the input is an alias, or the original input
 *                          if it's not an alias.
 */
export function mapFeatureEnumAlias(featureEnum: FeatureEnum): FeatureEnum {
  // Go through each entry in FeatureAliases
  for (const [key, aliases] of Object.entries(FeatureAliases)) {
    // If the FeatureEnum value is in the array of aliases, return the FeatureEnum key
    if (aliases.includes(featureEnum)) {
      return key as FeatureEnum;
    }
  }
  // If the FeatureEnum value is not an alias, return the original value
  return featureEnum;
}

/**
 * Checks whether the given feature is related to blueberries.
 *
 * @param feature - The feature to check.
 * @returns True if the feature is related to blueberries; false otherwise.
 */
export const isBlueberryFeature = (feature: FeatureEnum | null): boolean => {
  // Combine the blueberry-related features into one array
  const blueberryRelatedFeatures = [
    FeatureEnum.blueberry,
    ...BlueberryFeatures,
  ];

  // Check if the provided feature is in the array of blueberry-related features
  return feature !== null && blueberryRelatedFeatures.includes(feature);
};

/**
 * Checks whether the given feature is related to blueberries flowers.
 *
 * @param feature - The feature to check.
 * @returns True if the feature is related to blueberry flowers; false otherwise.
 */
export const isBlueberryFlowerFeature = (
  feature: FeatureEnum | null
): boolean => {
  // Combine the blueberry flower related features into one array
  const blueberryFlowerRelatedFeatures = [
    FeatureEnum.flower,
    ...BlueberryFlowerStageFeatures,
  ];

  // Check if the provided feature is in the array of blueberry-related features
  return feature !== null && blueberryFlowerRelatedFeatures.includes(feature);
};
